import React, { useEffect, useRef, useState } from 'react'
import { TextField } from '@mui/material'

const LENGTH = 6

export interface Props {
  onComplete: (code: string) => void
  onChange?: (code: string) => void
  disabled?: boolean
  error?: boolean
  autoFocus?: boolean
}

const OtpInput = ({
  onComplete,
  onChange,
  disabled = false,
  error = false,
  autoFocus = true
}: Props) => {
  const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(''))
  const inputs = useRef<Array<HTMLInputElement | null>>([])
  const completed = useRef(false)

  useEffect(() => {
    if (autoFocus && !disabled) inputs.current[0]?.focus()
  }, [autoFocus, disabled])

  const push = (next: string[]) => {
    setDigits(next)
    const code = next.join('')
    onChange?.(code)
    if (code.length === LENGTH && !next.includes('')) {
      if (!completed.current) {
        completed.current = true
        onComplete(code)
      }
    } else {
      completed.current = false
    }
  }

  const focusAt = (index: number) => {
    const target = inputs.current[Math.max(0, Math.min(LENGTH - 1, index))]
    target?.focus()
    target?.select()
  }

  const handleChange = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '')
    if (!value) {
      // a non-digit keystroke leaves the box unchanged
      if (raw !== '') return
      const next = [...digits]
      next[index] = ''
      push(next)
      return
    }
    const next = [...digits]
    // typing over a filled box, or a multi-character insert, spills forward
    value.split('').forEach((char, offset) => {
      if (index + offset < LENGTH) next[index + offset] = char
    })
    push(next)
    focusAt(index + value.length)
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const next = [...digits]
      if (next[index]) {
        next[index] = ''
        push(next)
      } else if (index > 0) {
        next[index - 1] = ''
        push(next)
        focusAt(index - 1)
      }
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusAt(index - 1)
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusAt(index + 1)
    }
  }

  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '')
    if (!pasted) return
    event.preventDefault()
    const next = [...digits]
    pasted
      .slice(0, LENGTH - index)
      .split('')
      .forEach((char, offset) => {
        next[index + offset] = char
      })
    push(next)
    focusAt(index + pasted.length)
  }

  return (
    <div
      className="flex gap-2 sm:gap-3 justify-center"
      data-testid="automation-IRHQ_TwoFactor-Code"
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          value={digit}
          disabled={disabled}
          error={error}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          inputProps={{
            'aria-label': `Digit ${index + 1}`,
            inputMode: 'numeric',
            maxLength: 1,
            className: 'text-center text-l font-semibold px-0 py-3'
          }}
          inputRef={(element: HTMLInputElement | null) => {
            inputs.current[index] = element
          }}
          onChange={event => handleChange(index, event.target.value)}
          onKeyDown={event => handleKeyDown(index, event)}
          onPaste={event => handlePaste(index, event)}
          onFocus={event => event.target.select()}
          className="w-[2.75rem] sm:w-[3.25rem]"
          InputProps={{ className: 'rounded-[0.625rem]' }}
        />
      ))}
    </div>
  )
}

export default OtpInput
